import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import type { ListAdminInstructors } from '@workspace/client';
import { Button, DialogFooter, Input, Label, Textarea } from '@workspace/shadcn';
import {
    FieldMessage,
    FormMessage,
    ImageField,
    errorToFormIssue,
    useAction,
} from '@workspace/ui';

import { CreateDialog } from '../../components/create-dialog.tsx';

type Instructor = ListAdminInstructors['records'][number];

const schema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  position: z.string().min(1, { message: 'Position is required' }),
  institution: z.string().min(1, { message: 'Institution is required' }),
  bio: z.string().min(1, { message: 'Bio is required' }),
  imageUrl: z.string().nullable(),
});

/**
 * One dialog serves both modes: with no `instructor` it creates, with one it
 * patches. The two forms carry identical fields, so splitting them would only
 * duplicate the markup.
 */
export function InstructorDialog({
  instructor,
  trigger,
  open,
  onOpenChange,
}: {
  instructor?: Instructor;
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = open ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const createAction = useAction('POST /admin/instructors', {
    invalidate: ['GET /admin/instructors'],
  });
  const updateAction = useAction('PATCH /admin/instructors/{id}', {
    invalidate: ['GET /admin/instructors'],
  });

  const form = useForm({
    defaultValues: {
      name: instructor?.name ?? '',
      position: instructor?.position ?? '',
      institution: instructor?.institution ?? '',
      bio: instructor?.bio ?? '',
      imageUrl: instructor?.imageUrl ?? null,
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          if (instructor) {
            await updateAction.mutateAsync({ id: instructor.id, ...value });
            toast.success('Instructor updated');
          } else {
            await createAction.mutateAsync(value);
            toast.success('Instructor created');
          }
          setOpen(false);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={isOpen}
      onOpenChange={setOpen}
      title={instructor ? 'Edit Instructor' : 'Add Instructor'}
      subtitle={
        instructor
          ? 'Update the instructor shown on the public website.'
          : 'Create an instructor you can attach to courses.'
      }
      trigger={trigger}
    >
      <form.Field
        name="name"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Basel Abu Jamous"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <form.Field
          name="position"
          children={(field) => (
            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <Input
                id="position"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="Senior Data Scientist"
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="institution"
          children={(field) => (
            <div className="space-y-2">
              <Label htmlFor="institution">Institution</Label>
              <Input
                id="institution"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="KFSHI"
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />
      </div>

      <form.Field
        name="bio"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              rows={4}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Background and expertise"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="imageUrl"
        children={(field) => (
          <div className="max-w-xs space-y-2">
            <ImageField
              label="Photo"
              value={field.state.value}
              onChange={field.handleChange}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting
                ? 'Saving...'
                : instructor
                  ? 'Update'
                  : 'Create'}
            </Button>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}
